"use client";

import { useState, useRef, useEffect } from "react";
import {
  IconButton,
  Paper,
  ClickAwayListener,
  Portal,
  useMediaQuery,
  Typography,
  Tooltip,
  Stack,
  Button,
} from "@mui/material";
import { QrCode as QrCodeIcon, Download, WhatsApp } from "@mui/icons-material";
import { QRCodeCanvas } from "qrcode.react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";

export default function UserQRCode({ qrCodeString }) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const qrRef = useRef(null);
  const isMobile = useMediaQuery("(max-width:600px)");
  const [position, setPosition] = useState(null);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);

  useEffect(() => {
    if (buttonRef.current && open && !isMobile) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.top - 240,
        left: rect.left + rect.width / 2 - 100,
      });
    }

    if (isMobile) {
      setPosition({ top: 0, left: 0 });
    }
  }, [open, isMobile]);

  /* ---------------- Download QR ---------------- */
  const handleDownload = () => {
    if (!qrRef.current) return;

    const url = qrRef.current.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = url;
    link.download = "qr-code.png";
    link.click();
  };

  const handleWhatsAppShare = async () => {
    if (!qrRef.current) return;

    const canvas = qrRef.current;
    const dataUrl = canvas.toDataURL("image/png");
    const blob = await (await fetch(dataUrl)).blob();
    const file = new File([blob], "qr-code.png", { type: "image/png" });

    const isMobile =
      /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
      (navigator.maxTouchPoints > 0 && window.innerWidth < 768);

    /* ---------- MOBILE ---------- */
    if (
      isMobile &&
      navigator.share &&
      navigator.canShare &&
      navigator.canShare({ files: [file] })
    ) {
      try {
        await navigator.share({
          title: "QR Code",
          text: "Scan this QR to connect",
          files: [file],
        });
        return;
      } catch {
        return;
      }
    }

    /* ---------- DESKTOP ---------- */
    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = "qr-code.png";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // show modal instead of redirect
    setShowWhatsAppModal(true);
  };

  return (
    <>
      {/* QR Icon */}
      <Tooltip title="Show QR Code" arrow>
        <IconButton size="small" ref={buttonRef} onClick={() => setOpen(true)}>
          <QrCodeIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* Popover */}
      {open && (isMobile || position) && (
        <Portal>
          <ClickAwayListener onClickAway={() => setOpen(false)}>
            <Paper
              sx={{
                position: "fixed",
                top: isMobile ? "50%" : position.top,
                left: isMobile ? "50%" : position.left,
                transform: isMobile ? "translate(-50%, -50%)" : "none",
                zIndex: 9999,
                p: 2,
                borderRadius: 2,
                width: isMobile ? 300 : 220,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <QRCodeCanvas
                ref={qrRef}
                value={qrCodeString}
                size={isMobile ? 220 : 180}
                level="H"
                includeMargin
              />

              <Typography variant="caption" color="text.secondary">
                Scan QR to connect
              </Typography>

              {/* Actions */}
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Button size="small" startIcon={<Download />} onClick={handleDownload}>
                  Download
                </Button>

                <Button
                  size="small"
                  color="success"
                  startIcon={<WhatsApp />}
                  onClick={handleWhatsAppShare}
                >
                  WhatsApp
                </Button>
              </Stack>
            </Paper>
          </ClickAwayListener>
        </Portal>
      )}

      <Dialog open={showWhatsAppModal} onClose={() => setShowWhatsAppModal(false)}>
        <DialogTitle>QR downloaded</DialogTitle>

        <DialogContent>
          <DialogContentText>
            Your QR code has been downloaded.
            <br />
            To share it, upload the image in WhatsApp Web.
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setShowWhatsAppModal(false)}>Cancel</Button>

          <Button
            style={{ color: "white" }}
            variant="contained"
            onClick={() => {
              window.open(
                `https://wa.me/?text=${encodeURIComponent(
                  "I've attached a QR code. Please scan it."
                )}`,
                "_blank"
              );
              setShowWhatsAppModal(false);
            }}
          >
            Go to WhatsApp Web
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
